// src/api/borrowerPortal/applications.js
import { api, hasAuthenticatedCookieSession } from "../client.js";

const APPLICATIONS_RESOURCE = "/api/resource/Applications";

async function fetchApplicationsResource(params = {}, { forceTokenAuth = false } = {}) {
  const q = { ...params };
  if (q.applicant != null) {
    q.filters = JSON.stringify([["applicant", "=", q.applicant]]);
    delete q.applicant;
  }
  const res = await api.get(APPLICATIONS_RESOURCE, {
    params: q,
    _forceTokenAuth: forceTokenAuth,
  });
  const raw = res?.data?.data ?? res?.data?.message ?? res?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.data)) return raw.data;
  if (raw && typeof raw === "object" && "data" in raw) return raw.data || [];
  return [];
}

/**
 * List Applications. Uses: GET /api/resource/Applications
 * @param {Object} params - Query params (filters, fields, order_by, limit_page_length, limit_start, applicant, etc.)
 * @returns {Promise<Array|Object>} List of application docs (array), or raw response if API returns wrapped shape
 */
export async function listApplications(params = {}) {
  try {
    return await fetchApplicationsResource(params);
  } catch (err) {
    // Cookie session preferred for resource APIs; if that 403s, retry with token auth.
    if (err?.status === 403 && hasAuthenticatedCookieSession()) {
      return await fetchApplicationsResource(params, { forceTokenAuth: true });
    }
    throw err;
  }
}

/**
 * Get a single Application by name. Uses: GET /api/resource/Applications/:name
 * @param {string} name - Document name (e.g. APP-2025-00001)
 * @returns {Promise<Object|null>}
 */
export async function getApplication(name) {
  if (!name || !String(name).trim()) return null;
  const res = await api.get(`${APPLICATIONS_RESOURCE}/${encodeURIComponent(name)}`);
  return res?.data?.data ?? res?.data ?? null;
}

/**
 * Frappe Version rows for an Application — used to recover per-stage status change times.
 * @param {string} name - Application document name
 * @returns {Promise<Array>}
 */
export async function getApplicationStatusVersions(name) {
  if (!name || !String(name).trim()) return [];

  const fetchVersions = async ({ forceTokenAuth = false } = {}) => {
    const res = await api.get("/api/resource/Version", {
      params: {
        filters: JSON.stringify([
          ["ref_doctype", "=", "Applications"],
          ["docname", "=", String(name).trim()],
        ]),
        fields: JSON.stringify(["name", "creation", "modified", "data"]),
        order_by: "creation asc",
        limit_page_length: 100,
      },
      _forceTokenAuth: forceTokenAuth,
    });
    return res?.data?.data ?? [];
  };

  try {
    return await fetchVersions();
  } catch (err) {
    if (err?.status === 403 && hasAuthenticatedCookieSession()) {
      try {
        return await fetchVersions({ forceTokenAuth: true });
      } catch (retryErr) {
        console.warn(
          "[applications] Could not load application versions (token retry):",
          retryErr?.message || retryErr
        );
        return [];
      }
    }
    console.warn("[applications] Could not load application versions:", err?.message || err);
    return [];
  }
}

const STATUS_DATES_PATH = "/api/method/dynesourcerapi.application_status.get_application_status_dates";
const STATUS_APPLICATIONS_PATH = "/api/method/dynesourcerapi.application_status.get_applications";

/**
 * Date and time each stage was reached for one application.
 * Uses: GET dynesourcerapi.application_status.get_application_status_dates?name=<application>
 * @param {string} name - Application document name
 * @returns {Promise<Object|Array|null>} Raw `message` payload, or null when unavailable
 */
export async function getApplicationStatusDates(name) {
  if (!name || !String(name).trim()) return null;
  try {
    const res = await api.get(STATUS_DATES_PATH, { params: { name: String(name).trim() } });
    return res?.data?.message ?? res?.data ?? null;
  } catch (err) {
    console.warn("[applications] Could not load application status dates:", err?.message || err);
    return null;
  }
}

/**
 * Applications visible to the current user, with their per-stage dates.
 * Uses: GET dynesourcerapi.application_status.get_applications
 * @returns {Promise<Array>}
 */
export async function getApplicationsWithStatusDates() {
  try {
    const res = await api.get(STATUS_APPLICATIONS_PATH);
    const raw = res?.data?.message ?? res?.data;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.applications)) return raw.applications;
    if (Array.isArray(raw?.data)) return raw.data;
    return [];
  } catch (err) {
    console.warn("[applications] Could not load applications status list:", err?.message || err);
    return [];
  }
}

/**
 * Create an Application. Uses: POST /api/resource/Applications
 * @param {Object} payload - Application doc fields
 * @returns {Promise<Object>} Created doc
 */
export async function createApplication(payload) {
  const res = await api.post(APPLICATIONS_RESOURCE, payload);
  return res?.data?.data ?? res?.data ?? res?.data?.message ?? null;
}

/**
 * Update an Application. Uses: PUT /api/resource/Applications/:name
 * @param {string} name - Document name
 * @param {Object} payload - Fields to update
 * @returns {Promise<Object>} Updated doc
 */
export async function updateApplication(name, payload) {
  if (!name || !String(name).trim()) throw new Error("Application name is required");
  const res = await api.put(`${APPLICATIONS_RESOURCE}/${encodeURIComponent(name)}`, payload);
  return res?.data?.data ?? res?.data ?? res?.data?.message ?? null;
}

/**
 * Delete an Application. Uses: DELETE /api/resource/Applications/:name
 * @param {string} name - Document name
 * @returns {Promise<void>}
 */
export async function deleteApplication(name) {
  if (!name || !String(name).trim()) throw new Error("Application name is required");
  await api.delete(`${APPLICATIONS_RESOURCE}/${encodeURIComponent(name)}`);
}

/**
 * Submit an application (update with final payload, e.g. status or full form data).
 * Uses: PUT /api/resource/Applications/:name
 * @param {string} name - Application document name
 * @param {Object} payload - Fields to submit (formData, status, etc.)
 * @returns {Promise<Object>} Updated doc
 */
export async function submitApplication(name, payload) {
  return updateApplication(name, payload);
}
